import { useState } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { Sparkles, Lock, ArrowRight, Loader2 } from 'lucide-react';
import { startCheckout, isLifetimeTier } from '../../lib/billing';

// Two paywall states keyed off the user's effective tier:
//   lifetime_*    → "Add Cloud & AI for $4.99/mo" — the add-on path
//   free          → "Get Pro Lifetime — $70" — drive to a lifetime license first
//
// The component starts the right Stripe Checkout directly. Falling back to
// /account/billing only if Stripe isn't configured yet (preserves dev UX).
export default function AiPaywall({ tier = 'free', stripeEnabled = true }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const onLifetime = isLifetimeTier(tier);
  const product = onLifetime ? 'cloud_ai' : 'pro_lifetime';
  const ctaLabel = onLifetime
    ? 'Add Cloud & AI for $4.99/mo'
    : 'Get Pro Lifetime — $70';
  const heading = onLifetime
    ? 'AI tools require the Cloud & AI add-on'
    : 'AI tools come with the Cloud & AI add-on';
  const body = onLifetime
    ? 'Your lifetime license covers PDF tools and eSign. Add the Cloud & AI subscription to unlock chat, summaries, translation, and more — without giving up your lifetime entitlement.'
    : 'AI (chat, summarize, translate, quiz) is part of the $4.99/mo Cloud & AI add-on — available with any lifetime license. Get a license (Lite $49 / Pro $70), then add Cloud & AI.';

  const handleClick = async () => {
    if (!stripeEnabled) {
      navigate('/account/billing');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await startCheckout(product);
    } catch (err) {
      setError(err.message || 'Could not start checkout.');
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-full px-6 py-10 text-center">
      <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
        <Lock className="w-6 h-6 text-indigo-600" />
      </div>
      <h2 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-indigo-600" />
        {heading}
      </h2>
      <p className="text-sm text-gray-600 max-w-xs mb-5">{body}</p>
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 text-white px-5 py-2 text-sm font-semibold hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : ctaLabel}
        {!busy && <ArrowRight className="w-4 h-4" />}
      </button>
      {error && <p className="text-xs text-red-600 mt-3 max-w-xs">{error}</p>}
      <ul className="mt-6 text-xs text-gray-500 space-y-1 text-left">
        <li>· Chat with PDF · Summarize · Translate</li>
        <li>· Generate quiz questions · AI PDF Assistant</li>
      </ul>
    </div>
  );
}
